/**
 * LastSeenTimestampListener
 *
 * App-level listener that reports the signed-in user's "last seen" timestamp
 * to the backend when the app backgrounds — the web port of Flutter's
 * `MainScreenState.didChangeAppLifecycleState` last-seen trigger
 * (lib/presentation/screens/main/main_screen.dart:153-166).
 *
 * Flutter registers a `WidgetsBinding` observer for the whole `MainScreen`
 * session (main_screen.dart:113, removed on dispose :170) and, on
 * `AppLifecycleState.inactive`, runs three gates in order before firing:
 *   1. `if (kDebugMode) return;` (main_screen.dart:155-157)
 *   2. `if (RemoteConfigService.lastSeenEnabled)` (main_screen.dart:161)
 *   3. `CloudFunctionsService.triggerLastSeenTimestamp(platform)` (main_screen.dart:163)
 *
 * Web mapping. The established convention for Flutter `.inactive`/`.paused` is
 * the browser tab becoming hidden (`useChatPresence.ts:15-17` via
 * `subscribeToTabVisibility`), so the primary parity trigger is the
 * tab-hidden transition. We also fire on `beforeunload` — the tab-closing
 * analog of going inactive right before teardown. Both paths run the same
 * gated `reportLastSeen` callback.
 *
 * Renders nothing — mount once for the app session in `AppRoutes`, alongside
 * `WalletListener` / `PremiumPackagesListener` / `UserDeviceListener` (the web
 * home for `MainScreen`'s app-boot wiring). Gated on `isAuthenticated` because
 * Flutter's `MainScreen` only exists post-auth.
 */
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/presentation/store';
import { useTabVisibility, useBeforeUnload } from '@/presentation/utils/tabVisibility';
import { triggerLastSeenTimestampUseCase } from '@/domain/usecases/auth/triggerLastSeenTimestampUseCase';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import { COMMON_CONSTANTS } from '@/presentation/constants/commonConstants';

export const LastSeenTimestampListener = () => {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  // Mirrors Flutter's didChangeAppLifecycleState gates in order
  // (main_screen.dart:153-166):
  const reportLastSeen = useCallback(() => {
    // (1) Skip in dev — web analog of Flutter `if (kDebugMode) return;`
    // (main_screen.dart:155-157). Don't pollute last-seen data from
    // developer sessions.
    if (import.meta.env.DEV) return;
    // (2) Skip when the remote-config flag is off — Flutter
    // `if (RemoteConfigService.lastSeenEnabled)` (main_screen.dart:161). Read
    // from the live Remote Config template (falling back to the compile-time
    // `REMOTE_CONFIG_DEFAULTS` entry), no longer from a hardcoded constant, so
    // the flag can be flipped server-side exactly as it is on Flutter. Read at
    // callback time rather than at render, so a template that activates after
    // mount takes effect on the next tab-hidden transition.
    if (!remoteConfigService.lastSeenEnabled) return;
    // (3) Only report for a signed-in user — Flutter's `MainScreen` only
    // exists post-auth.
    if (!isAuthenticated) return;

    // Fire-and-forget, matching Flutter's `CloudFunctionsService
    // .triggerLastSeenTimestamp(platform)` (main_screen.dart:163). Flutter
    // sends `Theme.of(context).platform.index`; web has no faithful
    // equivalent, so we send the server's `-1` "unspecified platform"
    // sentinel via `COMMON_CONSTANTS.webPlatformCode` — a fixed protocol
    // sentinel describing this client, not a server-tunable Remote Config value.
    // The use case swallows errors (service logs via appLogger), so an
    // in-flight call during unload that does not complete is acceptable.
    triggerLastSeenTimestampUseCase.execute(COMMON_CONSTANTS.webPlatformCode);
  }, [isAuthenticated]);

  // Primary AppLifecycleState.inactive analog: the tab-hidden transition.
  useTabVisibility((isVisible) => {
    if (!isVisible) reportLastSeen();
  });

  // Tab-closing analog: page unload (reload, close, navigate away).
  useBeforeUnload(reportLastSeen);

  return null;
};
